export interface AppConfig {
  publicUrl: string;
  auth: {
    authority: string;
    response_type: string;
    client_id: string;
    redirect_uri: string;
    post_logout_redirect_uri: string;
    scope: string;
  };
  disseminationApiUrl: string;
  nexusApiUrl: string;
}

interface FrontendConfigResponse {
  publicUrl?: string;
  authority?: string;
  clientId?: string;
  redirectUri?: string;
  postLogoutRedirectUri?: string;
  scope?: string;
  disseminationApiUrl?: string;
  nexusApiUrl?: string;
}

let cachedConfig: AppConfig | null = null;

const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

// Mapping of Portal and Nexus hostnames to their corresponding Nexus API URLs
const PORTAL_TO_NEXUS_URL: Record<string, string> = {
  'dev-portal.fefundinfo.com': 'https://dev-api-nexusdissemination.fefundinfo.com',
  'qa-portal.fefundinfo.com': 'https://qa-api-nexusdissemination.fefundinfo.com',
  'portal.fefundinfo.com': 'https://api-nexusdissemination.fefundinfo.com',
  'dev-nexusdissemination.fefundinfo.com': 'https://dev-api-nexusdissemination.fefundinfo.com',
  'qa-nexusdissemination.fefundinfo.com': 'https://qa-api-nexusdissemination.fefundinfo.com',
  'nexusdissemination.fefundinfo.com': 'https://api-nexusdissemination.fefundinfo.com'
};

// Mapping of Portal and Nexus hostnames to their corresponding Dissemination API URLs
const PORTAL_TO_DISSEMINATION_URL: Record<string, string> = {
  'dev-portal.fefundinfo.com': 'https://prep-efs-dissemination.fefundinfo.com',
  'qa-portal.fefundinfo.com': 'https://efs-dissemination-qa.fefundinfo.com',
  'portal.fefundinfo.com': 'https://efs-dissemination.fefundinfo.com',
  'dev-nexusdissemination.fefundinfo.com': 'https://prep-efs-dissemination.fefundinfo.com',
  'qa-nexusdissemination.fefundinfo.com': 'https://efs-dissemination-qa.fefundinfo.com',
  'nexusdissemination.fefundinfo.com': 'https://efs-dissemination.fefundinfo.com'
};

function getKnownHostname(): string | null {
  if (typeof window === 'undefined') return null;
  const hostname = window.location.hostname;
  return Object.keys(PORTAL_TO_DISSEMINATION_URL).includes(hostname) ? hostname : null;
}

// Only portal hostnames use the env-based shortcut (no server-side /api/config).
// Standalone nexus hostnames fetch auth config from the API server instead.
const PORTAL_ONLY_HOSTNAMES = new Set([
  'dev-portal.fefundinfo.com',
  'qa-portal.fefundinfo.com',
  'portal.fefundinfo.com'
]);
const knownHostname = getKnownHostname();
const isPortalHost = knownHostname !== null && PORTAL_ONLY_HOSTNAMES.has(knownHostname);
const isStandaloneNexusHost = knownHostname !== null && !PORTAL_ONLY_HOSTNAMES.has(knownHostname);

function loadFromEnv(): AppConfig {
  return {
    publicUrl: process.env.APP_PUBLIC_URL ?? '',
    auth: {
      authority: process.env.APP_AUTHORITY ?? '',
      response_type: 'code',
      client_id: process.env.APP_CLIENT_ID ?? '',
      redirect_uri: process.env.APP_REDIRECT_URI ?? '',
      post_logout_redirect_uri: process.env.APP_POST_LOGOUT_REDIRECT_URI ?? '',
      scope: process.env.APP_SCOPE ?? ''
    },
    disseminationApiUrl: process.env.APP_DISSEMINATION_API_URL ?? '',
    nexusApiUrl: process.env.APP_NEXUS_API_URL ?? ''
  };
}

export async function loadAppConfig(): Promise<AppConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (isLocalhost) {
    cachedConfig = loadFromEnv();
    return cachedConfig;
  }

  // When running inside AMS Portal, avoid calling the portal's /api/config
  // as the portal backend doesn't serve this module's config.
  // Derive the Nexus API URL and Dissemination API URL from the hostname.
  if (isPortalHost && knownHostname) {
    const envConfig = loadFromEnv();
    cachedConfig = {
      ...envConfig,
      nexusApiUrl: PORTAL_TO_NEXUS_URL[knownHostname] || envConfig.nexusApiUrl,
      disseminationApiUrl: PORTAL_TO_DISSEMINATION_URL[knownHostname]
    };
    return cachedConfig;
  }

  // Standalone nexus host: fetch auth config from the API server
  if (isStandaloneNexusHost && knownHostname) {
    const apiBaseUrl = PORTAL_TO_NEXUS_URL[knownHostname];
    try {
      const response = await fetch(`${apiBaseUrl}/api/config`);
      if (response.ok) {
        const cfg: FrontendConfigResponse = await response.json();
        cachedConfig = {
          publicUrl: cfg.publicUrl ?? '',
          auth: {
            authority: cfg.authority ?? '',
            response_type: 'code',
            client_id: cfg.clientId ?? '',
            redirect_uri: cfg.redirectUri ?? '',
            post_logout_redirect_uri: cfg.postLogoutRedirectUri ?? '',
            scope: cfg.scope ?? ''
          },
          disseminationApiUrl: PORTAL_TO_DISSEMINATION_URL[knownHostname] ?? cfg.disseminationApiUrl ?? '',
          nexusApiUrl: apiBaseUrl
        };
        return cachedConfig;
      }
    } catch (error) {
      console.error('Error loading config from API server:', error);
    }
    // Fallback to env config if API fetch fails
    const envConfig = loadFromEnv();
    cachedConfig = {
      ...envConfig,
      nexusApiUrl: apiBaseUrl,
      disseminationApiUrl: PORTAL_TO_DISSEMINATION_URL[knownHostname] ?? envConfig.disseminationApiUrl
    };
    return cachedConfig;
  }

  try {
    const response = await fetch('/api/config', { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Failed to load frontend config: ${response.status}`);
    }

    const cfg: FrontendConfigResponse = await response.json();
    cachedConfig = {
      publicUrl: cfg.publicUrl ?? '',
      auth: {
        authority: cfg.authority ?? '',
        response_type: 'code',
        client_id: cfg.clientId ?? '',
        redirect_uri: cfg.redirectUri ?? '',
        post_logout_redirect_uri: cfg.postLogoutRedirectUri ?? '',
        scope: cfg.scope ?? ''
      },
      disseminationApiUrl: cfg.disseminationApiUrl ?? '',
      nexusApiUrl: cfg.nexusApiUrl ?? ''
    };
    return cachedConfig;
  } catch (error) {
    console.error('Error loading app config:', error);
    cachedConfig = loadFromEnv();
    return cachedConfig;
  }
}

export function getAppConfig(): AppConfig {
  if (!cachedConfig) {
    throw new Error('App config not loaded. Call loadAppConfig() first.');
  }
  return cachedConfig;
}

const appConfig = loadFromEnv();

export default appConfig;
